import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../../context/FarmaciaAuthContext";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  farmGet,
  farmPost,
  STATO_CONSEGNA_LABELS,
  FASCIA_CONSEGNA_LABELS,
} from "./shared";
import { FarmaciaPageChrome } from "../../components/FarmaciaPageChrome";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Vista autista: solo logistica (struttura, indirizzo, contatto, fascia, colli, note, stato).
 * Nessun dato sanitario, ricette, terapie, Carepy o importi.
 */
export default function AutistaConsegneFarmacia() {
  const { token, user } = useFarmaciaAuth();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(params.get("data") || todayISO());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await farmGet("/farmacia/consegne/autista", token, { data });
      setItems(res.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [token, data]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setParams({ data }, { replace: true });
  }, [data, setParams]);

  const esito = async (id, tipo) => {
    setBusyId(id);
    try {
      await farmPost(`/farmacia/consegne/${encodeURIComponent(id)}/esito`, token, { esito: tipo });
      toast.success("Aggiornato");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Operazione fallita");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto max-w-lg space-y-4">
      <FarmaciaPageChrome backTo={user?.ruolo === "autista" ? null : "/farmacia/consegne"} backLabel="Consegne">
        <Input
          type="date"
          className="h-9 w-auto"
          value={data}
          onChange={(e) => setData(e.target.value)}
        />
      </FarmaciaPageChrome>

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">
          Nessuna consegna pianificata per questa data.
        </div>
      ) : (
        <ul className="space-y-3">
          {items.map((c) => {
            const st = STATO_CONSEGNA_LABELS[c.stato] || {
              label: c.stato,
              className: "bg-slate-50 text-slate-600 border-slate-200",
            };
            const done = ["consegnata", "parziale", "problema"].includes(c.stato);
            return (
              <li key={c.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="text-base font-semibold text-slate-900">{c.struttura_nome}</h2>
                  <Badge variant="outline" className={`text-[10px] ${st.className}`}>
                    {st.label}
                  </Badge>
                </div>
                <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-600">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                  {c.indirizzo || "Indirizzo non disponibile"}
                </p>
                {c.referente || c.telefono ? (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600">
                    <Phone className="h-4 w-4 shrink-0 text-slate-400" />
                    {[c.referente, c.telefono].filter(Boolean).join(" · ") || c.contatto}
                  </p>
                ) : c.contatto ? (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-600">
                    <Phone className="h-4 w-4 shrink-0 text-slate-400" />
                    {c.contatto}
                  </p>
                ) : null}
                <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <div className="text-[10px] uppercase text-slate-400">Fascia</div>
                    <div className="font-medium text-slate-800">
                      {FASCIA_CONSEGNA_LABELS[c.fascia_oraria] || c.fascia_oraria || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-400">Colli</div>
                    <div className="font-medium text-slate-800">{c.numero_colli ?? 0}</div>
                  </div>
                </div>
                {c.note_logistiche ? (
                  <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                    {c.note_logistiche}
                  </p>
                ) : null}
                {!done ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      disabled={busyId === c.id}
                      onClick={() => esito(c.id, "completa")}
                    >
                      Consegnata
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyId === c.id}
                      onClick={() => esito(c.id, "parziale")}
                    >
                      Parziale
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-rose-700"
                      disabled={busyId === c.id}
                      onClick={() => esito(c.id, "problema")}
                    >
                      Problema
                    </Button>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
